import { Component, DestroyRef, inject, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { MatDialogRef, MatDialogModule } from '@angular/material/dialog'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatSelectModule } from '@angular/material/select'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'

import { finalize } from 'rxjs/operators'

import { ChatService } from '@services/chat-service'
import { toDeviceIdArray } from '@utils/chat-utils'
import { ChatState } from '@states/chat-state'
import { OperatorDataState } from '@states/operator-data'

@Component({
	templateUrl: './chat-new-conversation.html',
	styleUrl: './chat-new-conversation.scss',
	standalone: true,
	imports: [
		ReactiveFormsModule,
		MatButtonModule,
		MatDialogModule,
		MatIconModule,
		MatFormFieldModule,
		MatInputModule,
		MatSelectModule,
		MatProgressSpinnerModule,
	],
})
export class ChatNewConversation {
	readonly #ref = inject<MatDialogRef<ChatNewConversation>>(MatDialogRef)
	readonly #chatService = inject(ChatService)
	readonly #chatState = inject(ChatState)
	readonly #operatorDataState = inject(OperatorDataState)
	readonly #destroyRef = inject(DestroyRef)

	readonly devices = this.#chatState.devices
	readonly isSubmitting = signal(false)

	readonly form = new FormGroup({
		type: new FormControl<ChatConversationType>('BROADCAST', Validators.required),
		deviceIds: new FormControl<number | number[]>([]),
		message: new FormControl<string>('', [Validators.required, Validators.minLength(1)]),
	})

	get selectedType(): ChatConversationType {
		return this.form.get('type')!.value!
	}

	get isDirectConversation(): boolean {
		return this.selectedType === 'DIRECT'
	}

	createConversation(): void {
		if (this.form.invalid || this.isSubmitting()) return

		const { type, deviceIds, message } = this.form.getRawValue()
		const requestPointUuid = this.#operatorDataState.metadata()?.requestPointUuid ?? ''
		const normalizedDeviceIds = toDeviceIdArray(deviceIds)

		const payload: CreateConversationPayload = {
			type: type!,
			senderRequestPointUuid: requestPointUuid,
			message: message!.trim(),
			...(normalizedDeviceIds.length ? { participantDeviceTrackingIds: normalizedDeviceIds } : {}),
		}

		this.isSubmitting.set(true)
		this.form.disable()

		this.#chatService.createConversation(payload).pipe(
			finalize(() => {
				this.isSubmitting.set(false)
				this.form.enable()
			}),
			takeUntilDestroyed(this.#destroyRef)
		).subscribe(({ conversationUuid }) => {
			this.#ref.close()
			this.#refreshAndOpenConversation(conversationUuid, requestPointUuid)
		})
	}

	#refreshAndOpenConversation(conversationUuid: string, requestPointUuid: string): void {
		this.#chatService.getConversations(requestPointUuid).pipe(
			takeUntilDestroyed(this.#destroyRef)
		).subscribe(conversations => {
			this.#chatState.conversations.set(conversations)
			this.#chatState.activeConversationUuid.set(conversationUuid)
			this.#loadMessages(conversationUuid)
		})
	}

	#loadMessages(conversationUuid: string): void {
		this.#chatState.isLoadingMessages.set(true)
		this.#chatService.getMessages(conversationUuid).pipe(
			finalize(() => this.#chatState.isLoadingMessages.set(false)),
			takeUntilDestroyed(this.#destroyRef)
		).subscribe(messages => this.#chatState.messages.set(messages))
	}
}
